import React, { useState } from "react";
import styled from "@emotion/styled";
import { Modal, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import moment from "moment";
import swal from "sweetalert";
import { useDispatch } from "react-redux";
import { customersOperations } from "state/ducks/store/customers";

const STATUS_LABELS = {
  CAPTURED: "Payé",
  AUTHORISED: "Autorisé",
  REFUNDED: "Remboursé",
  REFUSED: "Refusé",
  succeeded: "Payé",
};

/** Petit « ? » avec bulle d'aide expliquant une action. */
const Help = ({ id, text }) => (
  <OverlayTrigger placement="top" overlay={<Tooltip id={id}>{text}</Tooltip>}>
    <HelpBadge>?</HelpBadge>
  </OverlayTrigger>
);

/**
 * Popup d'édition d'une commande : détail + actions possibles (remboursement d'un paiement).
 * Reçoit le client et l'id de la commande, et dérive la commande à jour depuis customer.orders
 * (pour refléter automatiquement les changements après un rechargement).
 */
const PopupOrderEdit = (props) => {
  const { show, onHide, customer, orderId } = props;

  require("moment/locale/fr.js");

  const dispatch = useDispatch();
  const [refundingId, setRefundingId] = useState(null);
  const [cancellingAdvantage, setCancellingAdvantage] = useState(false);

  const order = (customer && customer.orders ? customer.orders : []).find(
    (o) => o.id === orderId
  );

  const handleRefund = (payment) => {
    swal({
      title: "Rembourser ce paiement ?",
      text: `${payment.type} — ${(payment.amount / 100).toFixed(2)} € sur la commande ${order.numberOrder || order.id}.\nLe remboursement sera envoyé au client via ${payment.type}.`,
      icon: "warning",
      buttons: ["Annuler", "Rembourser"],
      dangerMode: true,
    }).then((confirm) => {
      if (!confirm) {
        return;
      }
      setRefundingId(payment.id);
      dispatch(
        customersOperations.refundOrderPayment(customer.id, order.id, payment.id)
      ).then(
        (res) => {
          setRefundingId(null);
          const message =
            (res && res.data && res.data.message) || "Remboursement effectué.";
          swal({ title: "Remboursement effectué", text: message, icon: "success" });
          // Recharge les commandes pour refléter le nouveau statut du paiement
          dispatch(customersOperations.getStoreCustomerOrders(customer));
        },
        (err) => {
          setRefundingId(null);
          const message =
            (err && err.response && err.response.data && err.response.data.message) ||
            "Le remboursement a échoué.";
          swal({ title: "Échec du remboursement", text: message, icon: "error" });
        }
      );
    });
  };

  const handleCancelAdvantage = () => {
    swal({
      title: "Annuler l'utilisation de la cagnotte ?",
      text: `${(order.advantageUsed / 100).toFixed(2)} € d'avantage avaient été utilisés sur la commande ${order.numberOrder || order.id}.\nL'avantage sera rendu au client (réutilisable) et le prix de la commande remis au tarif plein (= le montant réellement payé), ce qui ramène le surplus à 0.`,
      icon: "warning",
      buttons: ["Annuler", "Re-créditer"],
      dangerMode: true,
    }).then((confirm) => {
      if (!confirm) {
        return;
      }
      setCancellingAdvantage(true);
      dispatch(
        customersOperations.cancelOrderAdvantage(customer.id, order.id)
      ).then(
        (res) => {
          setCancellingAdvantage(false);
          const message =
            (res && res.data && res.data.message) || "Avantage re-crédité au client.";
          swal({ title: "Avantage re-crédité", text: message, icon: "success" });
          dispatch(customersOperations.getStoreCustomerOrders(customer));
        },
        (err) => {
          setCancellingAdvantage(false);
          const message =
            (err && err.response && err.response.data && err.response.data.message) ||
            "L'opération a échoué.";
          swal({ title: "Échec", text: message, icon: "error" });
        }
      );
    });
  };

  return (
    <Popup show={show} onHide={onHide} onEscapeKeyDown={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {order
            ? `Commande ${order.numberOrder || order.id}`
            : "Commande"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!order ? (
          "Commande introuvable."
        ) : (
          <>
            <Info noGutters>
              <Col sm={6}><b>Commande pour le</b><p>{moment(order.date).format("DD-MM-YYYY")}</p></Col>
              <Col sm={6}><b>Prix TTC</b><p>{(order.price / 100).toFixed(2)} €</p></Col>
            </Info>
            <Info noGutters>
              <Col sm={6}>
                <Link href={order.link} target="_blank">Fiche prod</Link>
              </Col>
              <Col sm={6}>
                <Link href={order.invoicePublicLink} target="_blank">Facture</Link>
              </Col>
            </Info>

            <SectionTitle>
              Récapitulatif paiement
              <Help
                id={`help-surplus-${order.id}`}
                text="Payé = somme des paiements réussis (remboursés exclus). Dû = total TTC + pourboire − participation entreprise − cagnotte − ticket resto. Surplus = payé − dû (s'il est positif, le client a payé en trop)."
              />
            </SectionTitle>
            <Info noGutters>
              <Col sm={4}><b>Payé par le client</b><p>{((order.paidOnline || 0) / 100).toFixed(2)} €</p></Col>
              <Col sm={4}><b>Montant dû</b><p>{((order.dueOnline || 0) / 100).toFixed(2)} €</p></Col>
              <Col sm={4}>
                <b>Surplus</b>
                <p><Surplus over={(order.surplus || 0) > 0}>{((order.surplus || 0) / 100).toFixed(2)} €</Surplus></p>
              </Col>
            </Info>

            <SectionTitle>Paiements</SectionTitle>
            {order.payments && order.payments.length > 0 ? (
              order.payments.map((payment) => (
                <PaymentRow key={payment.id}>
                  <span>
                    {payment.type} — <b>{(payment.amount / 100).toFixed(2)} €</b>
                    {" · "}
                    <Status status={payment.status}>
                      {STATUS_LABELS[payment.status] || payment.status}
                    </Status>
                    {payment.amountRefunded > 0 && payment.status !== "REFUNDED" &&
                      ` (remboursé : ${(payment.amountRefunded / 100).toFixed(2)} €)`}
                  </span>
                  {payment.refundable &&
                    <Actions>
                      <Help
                        id={`help-refund-${payment.id}`}
                        text="Rembourse ce paiement au client (PayGreen ou Edenred). À utiliser pour rendre un paiement en trop : double paiement, ou Edenred superflu encaissé en plus de la CB."
                      />
                      <RefundButton
                        type="button"
                        disabled={refundingId === payment.id}
                        onClick={() => handleRefund(payment)}
                      >
                        {refundingId === payment.id ? "Remboursement…" : "Rembourser"}
                      </RefundButton>
                    </Actions>}
                </PaymentRow>
              ))
            ) : (
              "Aucun paiement enregistré."
            )}

            {order.advantageUsed > 0 &&
              <>
                <SectionTitle>Cagnotte / avantage utilisé</SectionTitle>
                <PaymentRow>
                  <span>
                    Avantage utilisé : <b>{(order.advantageUsed / 100).toFixed(2)} €</b>
                  </span>
                  <Actions>
                    <Help
                      id={`help-advantage-${order.id}`}
                      text="Annule la remise : l'avantage (cagnotte) utilisé est rendu au client (réutilisable) et le prix de la commande est remis au tarif plein (= ce qui a été réellement payé), ce qui ramène le surplus à 0. La facture B2C, générée à la volée, reflète automatiquement le nouveau TTC. À utiliser quand le client a payé plein tarif malgré l'avantage."
                    />
                    <RefundButton
                      type="button"
                      disabled={cancellingAdvantage}
                      onClick={handleCancelAdvantage}
                    >
                      {cancellingAdvantage ? "Re-créditation…" : "Annuler l'utilisation"}
                    </RefundButton>
                  </Actions>
                </PaymentRow>
              </>}
          </>
        )}
      </Modal.Body>
    </Popup>
  );
};

const Popup = styled(Modal)`
  .modal-content {
    background-color: #ecf0f5 !important;
  }
  p {
    margin-bottom: 0rem;
  }
`;

const Info = styled(Row)`
  margin-bottom: 0.75rem;
  b {
    color: grey;
    font-size: 0.75rem;
    text-transform: uppercase;
  }
`;

const SectionTitle = styled.h5`
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  border-top: 1px solid #ddd;
  padding-top: 0.75rem;
`;

const PaymentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  padding: 0.5rem 0.75rem;
  margin-bottom: 0.4rem;
  font-size: 0.9rem;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const HelpBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #999;
  color: #666;
  font-size: 0.7rem;
  font-weight: bold;
  cursor: help;
  flex: 0 0 auto;
`;

const Status = styled.span`
  font-weight: bold;
  color: ${(props) =>
    props.status === "REFUNDED"
      ? "#c0392b"
      : props.status === "REFUSED"
      ? "grey"
      : props.theme.colors.green};
`;

const Surplus = styled.span`
  font-weight: bold;
  color: ${(props) => (props.over ? "#c0392b" : props.theme.colors.green)};
`;

const RefundButton = styled.button`
  border: 1px solid #c0392b;
  color: #c0392b;
  background: white;
  border-radius: 4px;
  font-size: 0.82rem;
  padding: 0.15rem 0.7rem;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: #c0392b;
    color: white;
  }
  &:disabled {
    opacity: 0.6;
    cursor: progress;
    background: white;
    color: #c0392b;
  }
`;

const Link = styled.a`
  color: ${(props) => props.theme.colors.green};
  text-decoration: underline;
`;

PopupOrderEdit.propTypes = {};

export default PopupOrderEdit;
